// Milestones kanban — five columns matching the planner_milestone
// status enum. Cross-rock view: each card carries a parent-rock
// breadcrumb (R12 · Roll out CRM) so weekly milestone triage doesn't
// require opening every rock sheet. Drag across columns to change the
// milestone's status via `updateMilestone`, optimistic with refresh
// on settle. Clicking a card opens the parent rock sheet pinned to its
// Milestones tab.
import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { tpApi, type MilestoneCard } from "../api";
import { useResource } from "../hooks";
import { formatDateShort, isOverdue } from "../utils";
import { Avatar } from "../components/Avatar";
import { RockSheet } from "../sheets/RockSheet";
import { usePlannerRefreshKey } from "../sheets/PlannerCreateContext";
import {
  MILESTONE_STATUS,
  type Milestone,
} from "../../../../../../shared/team-planner/schemas";
import type { PlannerScopeId } from "../../../../../../shared/team-planner/const";

const COLUMNS: Array<{ id: Milestone["status"]; label: string }> = [
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In progress" },
  { id: "blocked", label: "Blocked" },
  { id: "done", label: "Done" },
  { id: "cancelled", label: "Cancelled" },
];

function statusChipClass(status: Milestone["status"]): string {
  // Reuse the same chip palette as the Rocks board so the visual
  // language stays consistent across the two boards.
  if (status === "done") return "tp-chip tp-chip-green";
  if (status === "blocked") return "tp-chip tp-chip-red";
  if (status === "in_progress") return "tp-chip tp-chip-blue";
  if (status === "cancelled") return "tp-chip tp-chip-grey";
  return "tp-chip tp-chip-amber";
}

function DroppableColumn({
  id,
  label,
  count,
  children,
}: {
  id: Milestone["status"];
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`tp-kanban-column${isOver ? " tp-droppable-over" : ""}`}
    >
      <div className="tp-kanban-col-head">
        <span>{label}</span>
        <span className="tp-kanban-col-count">{count}</span>
      </div>
      {children}
    </div>
  );
}

function DraggableMilestoneCard({
  milestone,
  onOpen,
}: {
  milestone: MilestoneCard;
  onOpen: (rockId: string) => void;
}) {
  const { setNodeRef, listeners, attributes, isDragging, transform } = useDraggable({
    id: milestone.id,
    data: { kind: "milestone", rockId: milestone.rockId },
  });
  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? "tp-dragging" : undefined}
      style={style}
      // 6px drag threshold (from the sensor) keeps a plain click
      // working — opens the parent rock sheet on its Milestones tab.
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onOpen(milestone.rockId);
      }}
    >
      <MilestoneCardBody milestone={milestone} />
    </div>
  );
}

function MilestoneCardBody({ milestone }: { milestone: MilestoneCard }) {
  const overdue = isOverdue(milestone.dueAt, milestone.status);
  return (
    <div className="tp-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
        <span
          className="bsp-mono"
          title={`Parent rock R${milestone.rockSeq}`}
          style={{
            font: "700 10px/1 var(--bsp-font-mono)",
            letterSpacing: "0.06em",
            color: "var(--bsp-blue)",
          }}
        >
          R{milestone.rockSeq}
        </span>
        <span className={statusChipClass(milestone.status)}>
          {milestone.status.replace("_", " ")}
        </span>
      </div>
      <div className="tp-card-title">{milestone.title}</div>
      <div
        style={{
          font: "500 11px/1.4 var(--bsp-font-sans)",
          color: "var(--bsp-fg-muted)",
          marginTop: 2,
        }}
        title={milestone.rockTitle}
      >
        {milestone.rockTitle}
      </div>
      {milestone.parentTeam ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            font: "700 9px/1 var(--bsp-font-sans)",
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--bsp-blue)",
            marginTop: 4,
          }}
        >
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              background: "var(--bsp-blue-tint)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 8,
              fontWeight: 800,
            }}
          >
            {(milestone.parentTeam.monogram ?? "··").toUpperCase()}
          </span>
          {milestone.parentTeam.name}
        </span>
      ) : null}
      <div className="tp-card-meta">
        <span style={{ color: overdue ? "var(--bsp-red-fg)" : undefined }}>
          Due {formatDateShort(milestone.dueAt)}
        </span>
        {milestone.primaryOwner ? <Avatar owner={milestone.primaryOwner} /> : null}
      </div>
    </div>
  );
}

export function MilestonesView({ scope }: { scope: PlannerScopeId }) {
  const [openRockId, setOpenRockId] = useState<string | null>(null);
  const refreshKey = usePlannerRefreshKey();
  const milestones = useResource(
    () => tpApi.listAllMilestones(scope),
    [scope, refreshKey],
  );

  // Optimistic status overrides during DnD. Wiped on every refetch so
  // the server stays the source of truth post-mutation.
  const [statusOverrides, setStatusOverrides] = useState<Map<string, Milestone["status"]>>(
    new Map(),
  );
  useEffect(() => {
    setStatusOverrides(new Map());
  }, [milestones.data]);

  const grouped = useMemo(() => {
    const map = new Map<Milestone["status"], MilestoneCard[]>();
    for (const col of COLUMNS) map.set(col.id, []);
    for (const m of milestones.data ?? []) {
      const effective = statusOverrides.get(m.id) ?? m.status;
      map.get(effective)?.push(m);
    }
    return map;
  }, [milestones.data, statusOverrides]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  async function onDragEnd(e: DragEndEvent) {
    const id = String(e.active.id);
    const target = e.over?.id ? (String(e.over.id) as Milestone["status"]) : null;
    if (!target) return;
    const m = (milestones.data ?? []).find((row) => row.id === id);
    if (!m) return;
    if (m.status === target) return;
    setStatusOverrides((map) => {
      const next = new Map(map);
      next.set(id, target);
      return next;
    });
    try {
      await tpApi.updateMilestone(id, { status: target });
      milestones.refresh();
    } catch {
      setStatusOverrides((map) => {
        const next = new Map(map);
        next.delete(id);
        return next;
      });
      milestones.refresh();
    }
  }

  // Suppress unused-import warning if MILESTONE_STATUS ever drifts —
  // we read it indirectly via COLUMNS but want the import to stay so
  // bumping the enum in the schema raises a compile error here too.
  void MILESTONE_STATUS;

  return (
    <>
      {milestones.error ? (
        <p style={{ color: "var(--bsp-red-fg)" }}>
          Failed to load milestones: {milestones.error}
        </p>
      ) : null}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="tp-kanban">
          {COLUMNS.map((col) => {
            const items = grouped.get(col.id) ?? [];
            return (
              <DroppableColumn key={col.id} id={col.id} label={col.label} count={items.length}>
                {items.length === 0 ? (
                  <div className="tp-empty">No milestones</div>
                ) : (
                  items.map((m) => (
                    <DraggableMilestoneCard
                      key={m.id}
                      milestone={m}
                      onOpen={setOpenRockId}
                    />
                  ))
                )}
              </DroppableColumn>
            );
          })}
        </div>
      </DndContext>

      {openRockId ? (
        <RockSheet
          id={openRockId}
          quarter=""
          initialTab="milestones"
          onClose={() => setOpenRockId(null)}
          onChanged={() => milestones.refresh()}
        />
      ) : null}
    </>
  );
}
